import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { isEmpty, map } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { CircularProgress, Dialog, Divider, FlatButton, FontIcon, MenuItem, Paper, RaisedButton, SelectField, TextField } from 'material-ui';

import { useForm, Controller } from 'react-hook-form/dist/index.ie11';
import international from 'sort-international';

import styles from 'styles/TBenefits';
import { normalizeNumber } from '../../utils/normalize';

import Puzzle from '../../images/Puzzle';
import { createApprovalRequest } from '../../actions/ApprovalRequests';
import KTitle from '../../components/KJRN/KTitle';
import pallet from '../../constants/colors';

const countries = require('i18n-iso-countries');
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
countries.registerLocale(require('i18n-iso-countries/langs/is.json'));

const TEmployeeMasterCourseRequest = ({ loading, submitRequest }) => {
  const [showDialog, setShowDialog] = useState(false);
  const openDialog = () => setShowDialog(true);
  const closeDialog = () => setShowDialog(false);

  const {
    handleSubmit,
    errors,
    control,
    setValue,
  } = useForm();

  const actions = [
    <FlatButton
      label={<FormattedMessage id="alert.cancel" />}
      disabled={loading}
      secondary
      onClick={closeDialog}
    />,
    <RaisedButton
      label={<FormattedMessage id="submit" />}
      icon={<FontIcon className="material-icons">send</FontIcon>}
      disabled={loading || !isEmpty(errors)}
      primary
      onClick={handleSubmit(data => submitRequest(data, closeDialog))}
    />,
  ];

  const sortCountries = (a, b) => {
    const internationalCompare = international('text');
    return internationalCompare(a, b);
  };
  const handleNumberInput = name => (e) => {
    const { value } = e.target;
    if (value) {
      const cost = normalizeNumber(value);
      setValue(name, cost, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(name, '', { shouldValidate: true, shouldDirty: true });
    }
  };
  return (
    <div style={styles.container}>
      <Dialog
        open={showDialog}
        actions={actions}
        contentStyle={{
          overflow: 'hidden',
          maxWidth: 500,
          width: '100%',
        }}
        bodyStyle={{
          overflowY: 'auto',
        }}
        modal
      >
        {loading ? (
          <div
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.34)',
              position: 'absolute',
              zIndex: 10,
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Paper
              style={{
                padding: 32,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <CircularProgress style={{ marginBottom: 24 }} size={100} />
              <FormattedMessage id="sending.message" />
            </Paper>
          </div>
        ) : null}
        <KTitle
          style={{
            marginLeft: 0,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
          }}
          title={(
            <React.Fragment>
              <FontIcon
                style={{
                  marginRight: 6, fontSize: 28, color: 'inherit',
                }}
                className="material-icons"
              >school
              </FontIcon>
              <FormattedMessage id="dash.title.courseApprovalRequest" />
            </React.Fragment>
          )}
          subtitleStyle={{ margin: 0, fontSize: 12, lineHeight: '1' }}
          subtitle={(
            <React.Fragment>
              <FormattedMessage id="courseApprovalRequest.description" />
            </React.Fragment>
          )}
        />
        <form>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Controller
              as={(
                <TextField
                  fullWidth
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.title" />}
                  errorText={errors.RequestTitle ? <FormattedMessage id="editPanel.required" /> : ''}
                />
              )}
              name="RequestTitle"
              control={control}
              defaultValue=""
              rules={{ required: true }}
            />
            <Controller
              as={(
                <TextField
                  fullWidth
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.description" />}
                  errorText={errors.RequestDescription ? <FormattedMessage id="editPanel.required" /> : ''}
                  multiLine
                  rows={3}
                />
              )}
              name="RequestDescription"
              control={control}
              defaultValue=""
              rules={{ required: true }}
            />
            <Controller
              as={(
                <TextField
                  fullWidth
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.organizer" />}
                />
              )}
              name="Info"
              control={control}
              defaultValue=""
            />
            <Controller
              as={(
                <TextField
                  fullWidth
                  type="date"
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.from" />}
                  errorText={errors.PeriodBegda ? <FormattedMessage id="editPanel.required" /> : ''}
                />
              )}
              name="PeriodBegda"
              control={control}
              defaultValue=""
              rules={{ required: true }}
            />
            <Controller
              as={(
                <TextField
                  fullWidth
                  type="date"
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.to" />}
                  errorText={errors.PeriodEndda ? <FormattedMessage id="editPanel.required" /> : ''}
                />
              )}
              name="PeriodEndda"
              control={control}
              defaultValue=""
              rules={{ required: true }}
            />
            <Controller
              render={({
                onChange, onBlur, value, name, ref,
              }) => (
                <TextField
                  fullWidth
                  type="number"
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.totalHours" />}
                  min={0}
                  step={0.1}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  name={name}
                  inputRef={ref}
                />
              )}
              name="Duration"
              control={control}
            />
            <Controller
              render={({
                onChange, onBlur, value, name, ref,
              }) => (
                <SelectField
                  name={name}
                  onBlur={onBlur}
                  inputRef={ref}
                  onChange={(_, __, val) => onChange(val)}
                  fullWidth
                  floatingLabelFixed
                  value={value}
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.location" />}
                >
                  <MenuItem primaryText="Ísland" value="IS" key="IS.1" />
                  <Divider />
                  {map(map(countries.getNames('is'), (country, code) => ({ text: country, val: code })).sort(sortCountries), ({ text, val }) => (
                    <MenuItem key={val} primaryText={text} value={val} />
                  ))}
                </SelectField>
              )}
              name="Location"
              control={control}
              defaultValue=""
            />
            <Controller
              render={({
                onBlur, value, name, ref,
              }) => (
                <TextField
                  fullWidth
                  type="number"
                  floatingLabelFixed
                  floatingLabelText={<FormattedMessage id="approvalRequest.form.cost" />}
                  min={0}
                  onChange={(handleNumberInput(name))}
                  onBlur={onBlur}
                  value={value}
                  name={name}
                  inputRef={ref}
                />
              )}
              name="Cost"
              control={control}
            />
          </div>
        </form>
      </Dialog>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Puzzle />
        <div
          style={{
            padding: 8,
            marginBottom: 12,
            marginTop: 12,
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <FontIcon style={{ marginRight: 8 }} color={pallet.infoColor} className="material-icons" >info</FontIcon>
          <FormattedMessage id="courseApprovalRequest.description" />
        </div>
        <RaisedButton
          primary
          style={{ marginBottom: 12 }}
          onClick={openDialog}
          data-cy="employee-master-course-approval-request-button"
          label={<FormattedMessage id="courseApprovalRequest.button" />}
        />
      </div>
    </div>
  );
};

TEmployeeMasterCourseRequest.propTypes = {
  loading: PropTypes.bool.isRequired,
  submitRequest: PropTypes.func.isRequired,
};

TEmployeeMasterCourseRequest.defaultProps = {
};

export default connect(
  ({ approvalRequestReducer }) => approvalRequestReducer,
  dispatch => ({
    submitRequest: (data, cb) => dispatch(createApprovalRequest({
      ...data,
      EmployeeDetailApprovalRequestType: 'EmployeeMasterCoursesHandler',
    }, cb)),
  }),
)(TEmployeeMasterCourseRequest);



// WEBPACK FOOTER //
// ./src/containers/tiles/TEmployeeMasterCourseRequest.js